export type CodecType = 'avc1' | 'hev1' | 'hvc1' | 'unknown'

export interface CodecInfoI {
  type: CodecType
  mimeType: string
  isSupported: boolean
}

/**
 * MIME types for supported codecs
 * - avc1: H.264/AVC (widely supported)
 * - hev1/hvc1: H.265/HEVC (requires hardware decoder, Chrome 107+, Safari 13+, Firefox 133+)
 */
export const CODEC_MIME_TYPES: Record<CodecType, string> = {
  avc1: 'video/mp4; codecs="avc1.4D400D"',
  hev1: 'video/mp4; codecs="hev1.1.6.L123.B0"',
  hvc1: 'video/mp4; codecs="hvc1.1.6.L123.B0"',
  unknown: 'video/mp4',
}

/** Codec identifiers to search for in the fMP4 stsd box */
export const KNOWN_CODECS: CodecType[] = ['avc1', 'hev1', 'hvc1']

/** Minimum bytes needed to reliably detect codec in init segment */
export const MIN_INIT_SEGMENT_SIZE = 512

/** Maximum bytes to search for codec identifier */
export const MAX_CODEC_SEARCH_RANGE = 1024

/** Threshold before giving up on codec detection */
export const MAX_INIT_BUFFER_SIZE = 2048

/**
 * Combine multiple Uint8Array chunks into a single array
 */
export const combineChunks = (chunks: Uint8Array[]): Uint8Array => {
  const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0)
  const combined = new Uint8Array(totalLength)
  let offset = 0
  for (const chunk of chunks) {
    combined.set(chunk, offset)
    offset += chunk.length
  }
  return combined
}

/**
 * Read a 4-byte FourCC code at the given offset
 */
export const readFourCC = (data: Uint8Array, offset: number): string => {
  return String.fromCharCode(
    data[offset],
    data[offset + 1],
    data[offset + 2],
    data[offset + 3]
  )
}

/**
 * Detect codec type from fMP4 init segment by scanning for codec identifiers
 *
 * The codec identifier is in the sample description (stsd) box:
 * moov > trak > mdia > minf > stbl > stsd > [avc1|hev1|hvc1]
 */
export const detectCodecFromInitSegment = (data: Uint8Array): CodecType => {
  if (data.length < MIN_INIT_SEGMENT_SIZE) {
    return 'unknown'
  }

  const searchEnd = Math.min(data.length, MAX_CODEC_SEARCH_RANGE)

  for (let i = 0; i <= searchEnd - 4; i++) {
    const fourCC = readFourCC(data, i)
    if (KNOWN_CODECS.includes(fourCC as CodecType)) {
      return fourCC as CodecType
    }
  }

  return 'unknown'
}

/**
 * Get codec info including MIME type and browser support status
 */
export const getCodecInfo = (codecType: CodecType): CodecInfoI => {
  const mimeType = CODEC_MIME_TYPES[codecType]
  return {
    type: codecType,
    mimeType,
    isSupported:
      codecType !== 'unknown' && MediaSource.isTypeSupported(mimeType),
  }
}

/**
 * Build user-friendly error message for unsupported codec
 */
export const getUnsupportedCodecError = (codecType: CodecType): string => {
  const isH265 = codecType === 'hev1' || codecType === 'hvc1'
  return isH265
    ? 'This camera uses H.265/HEVC which is not supported by your browser. Try Chrome, Safari, or Edge with hardware decoding enabled.'
    : `Video codec "${codecType}" is not supported by your browser.`
}

/**
 * Handle QuotaExceededError by removing old buffered data
 */
export const removeOldBufferedData = (
  sourceBuffer: SourceBuffer,
  currentTime: number
): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      const { buffered } = sourceBuffer
      if (buffered.length === 0) {
        resolve()
        return
      }

      const start = buffered.start(0)
      const removeEnd = Math.max(start, currentTime - 30)

      if (removeEnd <= start) {
        resolve()
        return
      }

      sourceBuffer.addEventListener('updateend', () => resolve(), {
        once: true,
      })
      sourceBuffer.remove(start, removeEnd)
    } catch (err) {
      reject(err)
    }
  })
}

/**
 * Wait for SourceBuffer to finish updating
 */
export const waitForSourceBufferUpdate = (
  sourceBuffer: SourceBuffer
): Promise<void> => {
  if (!sourceBuffer.updating) {
    return Promise.resolve()
  }
  return new Promise((resolve) => {
    sourceBuffer.addEventListener('updateend', () => resolve(), { once: true })
  })
}
